import * as React from 'react';
import {faSearch} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useEffect, useMemo, useState} from 'react';

import useRecommendations from '../../items/item-spinner/use-recommendations';
import {CategorySpinner} from './CategorySpinner';
import {FilterChips} from './FilterChips';
import {GaItemListName} from '../../../client/ga/ga-ecommerce.functions';
import {GetRecoItemsParams} from '../../monetate/monetate.types';
import {ImpError} from '../../../client/imp-error/imp-error.class';
import {ImpUrl} from '../../../client/imp-url/imp-url.class';
import {ItemResult} from './ItemResult';
import {ItemSpinner} from '../../items/item-spinner/ItemSpinner';
import {ItemsService} from '../../../client/items/items.service';
import {MonetateService} from '../../../client/monetate/monetate.service';
import {Paginator} from '../../ui/Paginator/Paginator';
import {pushGaEvent} from '../../../client/ga/ga.functions';
import {Result, SearchResult} from '../search.types';
import {SortDropdown} from '../browse-sidebar/SortDropdown';
import {URLBuilder} from '../../../server/tools/url-builder/url-builder.class';
import {User} from '../../users/user.class';
import {useService} from '../../react/ServiceContext';
import {PageHeader} from '../../ui/PageHeader/PageHeader';

interface ItemResultsProps {
    buildURL: URLBuilder;
    debugWebcat: boolean;
    resultList?: SearchResult;
    stepIdPath: string;
    user: User;
}

export const ItemResults = ({buildURL, debugWebcat, resultList, stepIdPath, user}: ItemResultsProps) => {
    const [currentResultList, setCurrentResultList] = useState(resultList);
    const [priceLookupError, setPriceLookupError] = useState(``);
    const componentName = `ItemResults`;
    const itemsService: ItemsService = useService(`itemsService`);
    const monetateService: MonetateService = useService(`monetateService`);

    // Setup gaItemListName
    let gaItemListName = resultList.location as GaItemListName;
    if (resultList.featuredProduct) {
        gaItemListName = `Google Shopping Landing Page`;
    }

    // Get recommendations
    const getRecoItemsParams: GetRecoItemsParams = useMemo(() => {
        if (currentResultList.layout !== `campaign` && !currentResultList.categorySuggestions) {
            return {
                scheme: `category1_rr`,
                stepIdPath,
                webcat: currentResultList.webcatMeta?.name,
            };
        }
    }, [stepIdPath, currentResultList]);
    const recommendations = useRecommendations(getRecoItemsParams, monetateService);

    useEffect(() => {
        itemsService
            .addPricesForItemTypeArray(resultList.results)
            .then((newResults) => {
                resultList.results = newResults as Result[];
                setCurrentResultList({...resultList});
            })
            .catch((addPricesForItemTypeArrayErr: ImpError) => {
                setPriceLookupError(addPricesForItemTypeArrayErr.message);
            });
    }, [resultList, setCurrentResultList, itemsService]);

    // Send initial search results analytics events
    useEffect(() => {
        if (resultList) {
            const productIds = resultList.results.map((item) => {
                return {productId: item.id};
            });
            monetateService.searchResults(productIds, ImpUrl.getUrlSegment(1));
        }
        // Only run once per page view
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Template
     */
    return (
        <div data-e2e="search-results">
            {currentResultList.totalItems === 0 && <h1>0 results found for '{currentResultList.SearchString}'</h1>}
            <div className="tw-mb-6">
                <CategorySpinner
                    buildURL={buildURL}
                    debugWebcat={debugWebcat}
                    resultList={resultList}
                />
            </div>
            <FilterChips
                buildURL={buildURL}
                pushGaEvent={(gaEventOptions) =>
                    pushGaEvent(`ga4_navigation_facet`, {
                        ...gaEventOptions,
                        ...{
                            click_action: `remove_facet`,
                            click_category_1: `facet_chip`,
                            click_category_2: null,
                            click_path: location.pathname,
                            click_type: `remove_facet`,
                        },
                    })
                }
                resultList={currentResultList}
            />
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-bg-gray-100 tw-items-center tw-py-4 -tw-mx-4 lg:tw-mx-0 tw-px-2">
                <div className="tw-col-span-1 tw-w-full tw-relative tw-hidden md:tw-flex tw-items-center">
                    <label className="tw-whitespace-nowrap tw-mr-2 tw-mb-0">Sort By</label>
                    <SortDropdown
                        resultList={currentResultList}
                        user={user}
                    />
                </div>
                <div className="tw-col-span-1 tw-w-full tw-relative tw-hidden md:tw-block tw-text-center">
                    <Paginator
                        baseURL={buildURL.currentURL()}
                        currentPage={currentResultList.page}
                        itemsPerPage={50}
                        skipSanitize={true}
                        totalItems={currentResultList.totalItems}
                    />
                </div>
                <div className="tw-col-span-1 tw-w-full tw-relative tw-text-right">
                    <div>
                        {currentResultList.resultStart} - {currentResultList.resultEnd}
                        &nbsp;of&nbsp;{currentResultList.totalItems} results
                    </div>
                </div>
            </div>
            <div className="tw-px-4 tw-pb-1 tw-bg-gray-100 -tw-mx-4 lg:tw-mx-0">
                {currentResultList.results.map((item, itemIndex) => (
                    <ItemResult
                        gaItemListName={gaItemListName}
                        item={item}
                        itemIndex={itemIndex}
                        key={itemIndex}
                        position={itemIndex + currentResultList.resultStart}
                        priceLookupError={priceLookupError}
                        resultList={currentResultList}
                        user={user}
                    />
                ))}
            </div>
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-bg-gray-100 tw-items-center tw-mb-4 tw-pb-4 -tw-mx-4 lg:tw-mx-0 tw-px-2">
                <div className="tw-col-span-1 tw-col-start-1 lg:tw-col-start-2 tw-w-full tw-relative tw-order-2 sm:tw-order-1 tw-text-right sm:tw-text-left lg:tw-text-center tw-my-4">
                    <Paginator
                        baseURL={buildURL.currentURL()}
                        currentPage={currentResultList.page}
                        itemsPerPage={50}
                        skipSanitize={true}
                        totalItems={currentResultList.totalItems}
                    />
                </div>
                <div className="tw-col-span-1 tw-w-full tw-order-0 sm:tw-order-1 tw-text-right">
                    <div>
                        {currentResultList.resultStart} - {currentResultList.resultEnd}
                        &nbsp;of&nbsp;{currentResultList.totalItems} results
                    </div>
                </div>
            </div>
            {currentResultList.categorySuggestions?.length > 0 && (
                <>
                    <h3 className="tw-border-b tw-py-2 tw-font-bold">Related Searches</h3>
                    <div className="tw-overflow-x-auto tw-pt-2 tw-px-0 tw-pb-3 tw-flex">
                        {currentResultList.categorySuggestions.map((categorySuggestion, index) => (
                            <a
                                className="tw-my-0 tw-mx-2 tw-py-[13px] tw-px-3 tw-border tw-border-gray-700 tw-text-gray-700 tw-rounded-[50px] tw-whitespace-nowrap hover:tw-text-white [&_svg]:hover:!tw-text-white hover:tw-bg-gray-700 hover:tw-no-underline"
                                href={categorySuggestion.url}
                                key={index}
                            >
                                <FontAwesomeIcon
                                    className="tw-mr-1 tw-text-gray-700"
                                    color="#767676"
                                    icon={faSearch}
                                />
                                {categorySuggestion.name}
                            </a>
                        ))}
                    </div>
                </>
            )}
            {currentResultList.layout !== `campaign` && !currentResultList.categorySuggestions && (
                <div className="tw-mb-3">
                    <ItemSpinner
                        componentName={componentName}
                        gaItemListName={`scheme_${recommendations.schemeName}`}
                        label={recommendations.schemeExplanation}
                        loaded={recommendations.loaded}
                        maxSlides={5}
                        orderableItems={recommendations.items}
                        user={user}
                    />
                </div>
            )}
            {currentResultList.relatedCategories?.length > 0 && (
                <>
                    <PageHeader
                        title="Related Categories"
                        type="h4"
                    />
                    <div className="tw-overflow-x-auto tw-pt-2 tw-px-0 tw-pb-3 tw-flex">
                        {currentResultList.relatedCategories.map((categorySuggestion, index) => (
                            <a
                                key={index}
                                className="tw-my-0 tw-mx-2 tw-py-[13px] tw-px-3 tw-border tw-border-gray-700 tw-text-gray-700 tw-rounded-[50px] tw-whitespace-nowrap hover:tw-text-white hover:tw-bg-gray-700 hover:tw-no-underline"
                                href={`/browse/${categorySuggestion.categoryPath}`}
                            >
                                {categorySuggestion.name}
                            </a>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};
